import React from 'react';

const websiteUrl = process.env.REACT_APP_WEBSITE_URL || 'https://yourgermanaddress-website.vercel.app/';

const iconPaths = {
  overview: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  mailbox: <><path d="M4 5h16v13H4z" /><path d="m4 13 4-4h8l4 4" /></>,
  documents: <><path d="M6 2h9l4 4v16H6z" /><path d="M14 2v5h5M9 12h7M9 16h7" /></>,
  addresses: <><circle cx="12" cy="8" r="3" /><path d="M5 21v-2a7 7 0 0 1 14 0v2" /></>,
  verifications: <><path d="M9 11l2 2 4-4" /><path d="M12 22c5-2 8-6 8-11V5l-8-3-8 3v6c0 5 3 9 8 11z" /></>,
  company: <><path d="M4 21V3h11v18" /><path d="M15 9h5v12M8 7h3M8 11h3M8 15h3M3 21h18" /></>,
  display: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h10M7 13h6" /></>,
  subscriptions: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /></>,
  logistics: <><path d="M3 6h11v12H3zM14 10h4l3 3v5h-7z" /><circle cx="7" cy="19" r="2" /><circle cx="18" cy="19" r="2" /></>,
  guides: <><path d="M4 4h7a3 3 0 0 1 3 3v13a3 3 0 0 0-3-3H4z" /><path d="M20 4h-7a3 3 0 0 0-3 3v13a3 3 0 0 1 3-3h7z" /></>,
};

function HubIcon({ name }) {
  return (
    <svg aria-hidden="true" className="business-hub-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {iconPaths[name] || iconPaths.overview}
    </svg>
  );
}

export function BusinessHubShell({ activeTab, allowAdditionalServices = false, allowCompanyDataWorkflow = false, children, companyContextLabel, email, hasCompletedVerification, hasMailAccess = false, labels, logout, navigationCounts = {}, onCompanyContextAction, onTabChange }) {
  const navigationGroups = [
    { label: labels.groupStart, items: [['overview', labels.overview]] },
    { label: labels.groupMail, items: [['mailbox', labels.mailbox], ['documents', labels.documents], ['addresses', labels.addresses]] },
    { label: labels.groupCompany, items: [['company', labels.company], ['verifications', labels.verifications], ['display', labels.display]] },
    { label: labels.groupBilling, items: [['subscriptions', labels.subscriptions], ['logistics', labels.logistics]] },
    { label: labels.groupHelp, items: [['guides', labels.guides]] },
  ];
  const navigation = navigationGroups.flatMap((group) => group.items);
  const isLocked = (tabKey) => !hasCompletedVerification
    && !(hasMailAccess && ['mailbox', 'documents'].includes(tabKey))
    && !(allowAdditionalServices && tabKey === 'logistics')
    && !['overview', 'verifications', 'guides', 'subscriptions'].includes(tabKey)
    && !(allowCompanyDataWorkflow && tabKey === 'company');

  return (
    <div className="business-hub-shell" data-active-tab={activeTab}>
      <aside className="business-hub-sidebar">
        <a className="business-hub-brand" href={websiteUrl}>
          <span className="business-hub-brand__mark" aria-hidden="true">Y</span>
          <span>YourGermanAddress</span>
        </a>
        <nav className="business-hub-navigation" aria-label={labels.navigation}>
          {navigationGroups.map((group) => (
            <div className="business-hub-navigation__group" key={group.label}>
              <p className="business-hub-sidebar__label">{group.label}</p>
              {group.items.map(([tabKey, label]) => {
                const locked = isLocked(tabKey);
                return (
                  <button key={tabKey} type="button" aria-current={activeTab === tabKey ? 'page' : undefined}
                    className={`business-hub-navigation__item ${activeTab === tabKey ? 'is-active' : ''}`}
                    onClick={() => !locked && onTabChange(tabKey)} disabled={locked} title={locked ? labels.locked : undefined}>
                    <HubIcon name={tabKey} /><span>{label}</span>
                    {navigationCounts[tabKey] > 0 ? <span className="business-hub-navigation__count" aria-label={`${navigationCounts[tabKey]} offen`}>{navigationCounts[tabKey]}</span> : null}
                    {locked ? <span className="business-hub-navigation__lock" aria-hidden="true">•</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="business-hub-sidebar__footer">
          <span>{labels.signedIn}</span>
          <strong title={email}>{email}</strong>
        </div>
      </aside>

      <main className="business-hub-workspace" id="business-hub-main" tabIndex="-1">
        <header className="business-hub-topbar">
          <div>
            <p className="eyebrow">{labels.eyebrow}</p>
            <h1>{activeTab === 'overview' ? labels.title : navigation.find(([key]) => key === activeTab)?.[1]}</h1>
          </div>
          <div className="business-hub-topbar__actions">
            <a className="business-hub-website-link" href={websiteUrl}>{labels.website}</a>
            <button type="button" className="business-hub-signout" onClick={logout}>{labels.logout}</button>
          </div>
        </header>
        {companyContextLabel ? (
          <section className="business-hub-company-context" aria-label={labels.companyContext}>
            <div><span>{labels.companyContext}</span><strong>{companyContextLabel}</strong></div>
            <button type="button" onClick={onCompanyContextAction}>{labels.companyContextAction}</button>
          </section>
        ) : null}
        <div className="business-hub-mobile-nav-wrap">
          <label className="business-hub-mobile-nav">
            <span>{labels.mobileNavigation}</span>
            <select aria-label={labels.navigation} value={activeTab} onChange={(event) => onTabChange(event.target.value)}>
              {navigationGroups.map((group) => <optgroup label={group.label} key={group.label}>
                {group.items.map(([tabKey, label]) => <option value={tabKey} key={tabKey} disabled={isLocked(tabKey)}>{label}{navigationCounts[tabKey] > 0 ? ` (${navigationCounts[tabKey]})` : ''}</option>)}
              </optgroup>)}
            </select>
          </label>
        </div>
        <div className="business-hub-content">{children}</div>
      </main>
    </div>
  );
}
